'use client';

import React from 'react';
import Script, { ScriptProps } from 'next/script';
import { createGlobalStyle } from 'styled-components';
import { decamelizeKeys } from 'humps';
import { Trackable } from '../../types/trackable';
import { canUseWindow } from '../../wag-react/common/xenv';
import { Box, BoxProps } from '../../wag-react/next-components/box';

/**
 * This function can be used to manually initialize quote engine widget
 * Normally, you won't need to call since the quote engine widget
 * is initialized on page load
 */
export const initializeQuoteEngine = (urlParams?: Record<string, string>) => {
  if (!canUseWindow) {
    return;
  }
  const {
    QuoteEngineWag
  } = window;
  if (typeof QuoteEngineWag === 'undefined' || !QuoteEngineWag) {
    return;
  }
  QuoteEngineWag.setOptions({
    targetId: 'wag-quote-form',
    redirectUrl: 'https://quote.petinsurer.com/quote',
    baseUrl: 'https://quote.petinsurer.com',
    urlParam: {
      utm_source: '',
      utm_medium: '',
      utm_campaign: '',
      utm_content: '',
      utm_term: '',
      ...urlParams
    },
    refCode: 'wag'
  });
  QuoteEngineWag.init();
};
export type WagQuoteFormScriptProps = ScriptProps & {
  containerProps?: BoxProps;
  trackProps?: Trackable;
  initOnMount?: boolean;
};
export const WagQuoteFormScript = ({
  containerProps,
  initOnMount = true,
  trackProps,
  ...rest
}: WagQuoteFormScriptProps) => {
  const urlParams = JSON.stringify(decamelizeKeys(trackProps));
  return <>
      <GlobalStyle data-sentry-element="GlobalStyle" data-sentry-source-file="WagQuoteFormScript.tsx" />
      <Box width={1} {...containerProps} id="wag-quote-form" data-sentry-element="Box" data-sentry-source-file="WagQuoteFormScript.tsx" />
      <Script id="wag-quote-form-script" {...rest} dangerouslySetInnerHTML={{
      __html: `
          (function (doc, tag, id) {
            var js = doc.getElementsByTagName(tag)[0];
            if (doc.getElementById(id)) { return; }
            js = doc.createElement(tag); js.id = id;
            js.src = "https://quote.petinsurer.com/Scripts/lib/widgets/wag/quote-form/widget.min.js";
            js.async = true;
            js.type = "text/javascript";
            doc.body.appendChild(js);
          }(document, 'script', 'wag-quote-engine'));
          window.addEventListener("load", function (event) {
            if (typeof QuoteEngineWag === 'undefined') {
              // Failed to load Quote Form Widget
              // TODO - Error state of the Quote Form Widget
              console.error('Failed to load Quote Engine Side bar Widget');
              return;
            }

            ${initOnMount && `
              // It seems like we can't call the function 'initializeQuoteEngine' here
              // because it will be evaluated already since this is a template literal string

              QuoteEngineWag.setOptions({
                targetId: "wag-quote-form",
                redirectUrl: "https://quote.petinsurer.com/quote",
                baseUrl: "https://quote.petinsurer.com",
                params: ${urlParams},
                urlParam: ${urlParams},
                refCode: 'wag'
              });

              QuoteEngineWag.init();
            `}
          }, false);
          `
    }} data-sentry-element="Script" data-sentry-source-file="WagQuoteFormScript.tsx" />
    </>;
};

/**
 * Normalize the styles from the widget to avoid display inconsistencies
 */
const GlobalStyle = createGlobalStyle`
  /* Include the '#wag-quote-form' selector to have higher specificity */
  #wag-quote-form {
    .petinsurer-quote-form .formWidgetContainer {
      margin: 0;
      padding: 0;
      max-width: unset;
    }
    .petinsurer-quote-form .container-fluid {
      padding: 0;
    }
  }
`;